/**
 * usePrintables — React hook for browsing psecontent educational posters.
 * Catalog endpoints are public but include an `owned` flag per poster when a
 * signed-in user's token is present. Purchase + entitlement endpoints require
 * the token.
 */
import { API_BASE } from '../config.js';
import { useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

// Brightivity: the printables fork behind the front door (brightivity.dev/printables/<x> = /api/<x>).
const PRINTABLES_API = `${API_BASE}/printables`;

// Catalog-scope filters (contract 2026-08-19): language = CONTENT language
// FAMILY ('en'|'es'|'pt'|'fr'); 'all' means no filter, so it is omitted.
// jurisdiction = states.abbreviation ('TX'|'JAM'|'BRA'…). curriculum = the
// collapsed curriculum key the admin Gallery already emits. One definition so
// the poster list, search and both category endpoints can never drift apart.
function applyCatalogFilters(params, opts = {}) {
  if (opts.language && opts.language !== 'all') params.set('language', opts.language);
  if (opts.jurisdiction) params.set('jurisdiction', opts.jurisdiction);
  if (opts.curriculum) params.set('curriculum', opts.curriculum);
  // UI language → the worker answers with `displayName` beside every category
  // `name` it has a translation for (state document titles, course and strand
  // headings — 2026-09-08). Never a filter: counts and ids are unaffected.
  if (opts.ui) params.set('ui', opts.ui);
}

export default function usePrintables() {
  const { user } = useAuth();
  const token = user?.token || null;

  const [posters, setPosters] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const apiFetch = useCallback(async (endpoint, options = {}) => {
    const headers = { ...(options.headers || {}) };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (options.body && !headers['Content-Type']) headers['Content-Type'] = 'application/json';

    const res = await fetch(`${PRINTABLES_API}${endpoint.replace(/^\/api\//, '/')}`, { ...options, headers });
    // Parse defensively. A Cloudflare error page (1101 worker exception, 52x
    // origin errors, a WAF challenge) is HTML or plain text, not JSON — an
    // unconditional res.json() threw `SyntaxError: Unexpected token '<' …
    // <!DOCTYPE` straight into the UI (owner saw it 2026-09-08 under a pool
    // saturation). Every caller already handles a thrown Error with .status;
    // this just makes sure it IS that error, never a JSON parse crash.
    let json = null;
    try { json = await res.json(); } catch { json = null; }
    if (!res.ok || !json || !json.success) {
      const err = new Error(json?.error || `Request failed (${res.status})`);
      err.status = res.status;
      err.data = json?.data;
      throw err;
    }
    return json.data;
  }, [token]);

  // opts is optional — an unfiltered caller keeps hitting the bare URL.
  const fetchRootCategories = useCallback(async (opts = {}) => {
    const params = new URLSearchParams();
    applyCatalogFilters(params, opts);
    const qs = params.toString();
    const data = await apiFetch(`/api/categories/roots${qs ? `?${qs}` : ''}`);
    return data;
  }, [apiFetch]);

  const fetchCategoryChildren = useCallback(async (categoryId, opts = {}) => {
    const params = new URLSearchParams();
    applyCatalogFilters(params, opts);
    const qs = params.toString();
    const data = await apiFetch(`/api/categories/${categoryId}/children${qs ? `?${qs}` : ''}`);
    return data;
  }, [apiFetch]);

  const fetchCategory = useCallback(async (categoryId, opts = {}) => {
    const qs = opts.ui ? `?ui=${encodeURIComponent(opts.ui)}` : '';
    const data = await apiFetch(`/api/categories/${categoryId}${qs}`);
    return data;
  }, [apiFetch]);

  const fetchPosters = useCallback(async (opts = {}) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (opts.categoryId) params.set('category_id', opts.categoryId);
      // Curriculum browse tree (contract 2026-08-24). Absent ⇒ byte-identical
      // request to what shipped installers send, so every existing param keeps
      // its exact semantics. Server-side this is a plain AND, never a
      // replacement for category_id — the client only ever sends one (the two
      // selections are mutually exclusive in Printables.jsx).
      if (opts.curriculumNode) params.set('curriculum_node', opts.curriculumNode);
      if (opts.gradeMin != null) params.set('grade_min', String(opts.gradeMin));
      if (opts.gradeMax != null) params.set('grade_max', String(opts.gradeMax));
      if (opts.sort) params.set('sort', opts.sort);
      if (opts.page) params.set('page', String(opts.page));
      if (opts.limit) params.set('limit', String(opts.limit));
      applyCatalogFilters(params, opts);

      const qs = params.toString();
      const data = await apiFetch(`/api/posters${qs ? `?${qs}` : ''}`);
      setPosters(data.posters || []);
      setTotal(data.total || 0);
      setTotalPages(data.totalPages || 0);
      return data;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  const searchPosters = useCallback(async (opts = {}) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (opts.query) params.set('q', opts.query);
      if (opts.curriculumNode) params.set('curriculum_node', opts.curriculumNode);
      if (opts.gradeMin != null) params.set('grade_min', String(opts.gradeMin));
      if (opts.gradeMax != null) params.set('grade_max', String(opts.gradeMax));
      if (opts.page) params.set('page', String(opts.page));
      if (opts.limit) params.set('limit', String(opts.limit));
      applyCatalogFilters(params, opts);

      const qs = params.toString();
      const data = await apiFetch(`/api/posters/search?${qs}`);
      setPosters(data.posters || []);
      setTotal(data.total || 0);
      setTotalPages(data.totalPages || 0);
      return data;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  const fetchPoster = useCallback(async (posterId) => {
    const data = await apiFetch(`/api/posters/${posterId}`);
    return data;
  }, [apiFetch]);

  const fetchFeaturedPosters = useCallback(async () => {
    const data = await apiFetch('/api/posters/featured');
    return data;
  }, [apiFetch]);

  const fetchSimilarPosters = useCallback(async (posterId) => {
    const data = await apiFetch(`/api/posters/${posterId}/similar`);
    return data;
  }, [apiFetch]);

  // ─── Purchase + entitlements (auth required) ─────────────────────────
  const purchasePrintable = useCallback(async (posterId, idempotencyKey) => {
    if (!token) throw new Error('Not signed in');
    return await apiFetch(`/api/posters/${posterId}/purchase`, {
      method: 'POST',
      body: JSON.stringify({ idempotencyKey }),
    });
  }, [apiFetch, token]);

  const fetchEntitlements = useCallback(async () => {
    if (!token) throw new Error('Not signed in');
    return await apiFetch('/api/posters/entitlements');
  }, [apiFetch, token]);

  // ─── IIG Phase 4 — standard alignment chips + picker search ──────────
  // Public be_printables routes (standard codes are public); the token still
  // rides via apiFetch when present. Callers wrap these (generationFlow catches
  // fetchPosterAlignment) so a chip-less / search-less UI is the graceful
  // failure mode when the catalog worker is unreachable.

  // Single-poster alignment for the AI result chip + library-card stamp.
  const fetchPosterAlignment = useCallback(async (posterId, stateCode) => {
    const qs = stateCode ? `?state=${encodeURIComponent(stateCode)}` : '';
    const data = await apiFetch(`/api/posters/${posterId}/alignment${qs}`);
    return data?.alignment ?? null;
  }, [apiFetch]);

  // Batch alignment for a poster grid (≤48 ids). Maps posterId → alignment|null.
  const fetchPostersAlignment = useCallback(async (ids, stateCode) => {
    const list = (ids || []).filter(Boolean);
    if (!list.length) return {};
    const params = new URLSearchParams({ ids: list.join(',') });
    if (stateCode) params.set('state', stateCode);
    const data = await apiFetch(`/api/posters/alignment?${params.toString()}`);
    return data?.alignments ?? {};
  }, [apiFetch]);

  // Phase-5 ratings/favorites (spec §4.9). POST upserts; ≥1 of rating/favorite
  // required by the worker; source ∈ result_screen|post_print_toast|
  // printables_detail|library_card.
  const submitPosterFeedback = useCallback(async (posterId, { rating, favorite, source }) =>
    apiFetch(`/api/posters/${posterId}/feedback`, {
      method: 'POST',
      body: JSON.stringify({ rating, favorite, source }),
    }), [apiFetch]);

  const fetchPosterFeedback = useCallback(async (posterId) =>
    apiFetch(`/api/posters/${posterId}/feedback`), [apiFetch]);

  // Phase-5 search-demand logging — fire-and-forget after a page-1 catalog
  // search; the worker dedupes (10 min) and PII-filters. Never awaited by UI.
  const logSearch = useCallback((query, resultCount, opts = {}) => {
    try {
      apiFetch('/api/search-log', {
        method: 'POST',
        body: JSON.stringify({
          query,
          resultCount,
          gradeMin: opts.gradeMin ?? null,
          gradeMax: opts.gradeMax ?? null,
          page: opts.page ?? 1,
        }),
      }).catch(() => {});
    } catch { /* logging never affects search UX */ }
  }, [apiFetch]);

  // Intent-first generator (spec §5.2): curated non-curriculum node set for
  // one intent ('motivate' | 'classroom' | 'school'). Server-cached 1h.
  const fetchIntentList = useCallback(async (intent) => {
    const data = await apiFetch(`/api/categories/intent-list?intent=${encodeURIComponent(intent)}`);
    return data?.nodes ?? [];
  }, [apiFetch]);

  // Teach path: a state's raw subject-set spellings (for the chip pattern map
  // + the "More subjects…" reveal).
  const fetchStateSubjects = useCallback(async (code) => {
    const data = await apiFetch(`/api/states/${code}/subjects`);
    return data?.subjects ?? [];
  }, [apiFetch]);

  // Jurisdiction list (LatAm expansion 2026-07-27): US states + territories +
  // countries, straight from the DB (GET /api/states already returns them with
  // countryCode + jurisdictionType). Only jurisdictions that actually have
  // standards sets are useful in the picker, so filter setCount>0. Callers keep
  // a hardcoded US fallback for the offline case.
  const fetchJurisdictions = useCallback(async () => {
    const data = await apiFetch(`/api/states`);
    return (data?.states ?? []).filter((s) => (s.setCount ?? 0) > 0);
  }, [apiFetch]);

  // Catalog facets (contract 2026-08-19) — powers the Printables sidebar
  // dropdowns, their counts, and the empty-state diagnosis. NEVER throws: an
  // unreachable worker must degrade to a filter-less sidebar and today's
  // generic empty copy, not an error banner (same graceful-failure rule as the
  // alignment chips). `curriculum` is deliberately not sent — the curricula
  // axis is never narrowed by itself.
  const fetchCatalogFacets = useCallback(async (opts = {}) => {
    const params = new URLSearchParams();
    if (opts.language && opts.language !== 'all') params.set('language', opts.language);
    if (opts.jurisdiction) params.set('jurisdiction', opts.jurisdiction);
    const qs = params.toString();
    try {
      const data = await apiFetch(`/api/catalog/facets${qs ? `?${qs}` : ''}`);
      return {
        languages: data?.languages ?? [],
        jurisdictions: data?.jurisdictions ?? [],
        curricula: data?.curricula ?? [],
      };
    } catch {
      return { languages: [], jurisdictions: [], curricula: [] };
    }
  }, [apiFetch]);

  // ─── Curriculum browse tree (contract 2026-08-24) ────────────────────
  // The LEFT tree for a non-US jurisdiction: Subject → Document → Group →
  // Standard, straight from the ministry's own document set. Names come back in
  // the country's own language and are rendered verbatim.
  //
  // Node ids are OPAQUE (`cur.v1.<type>.<JUR>[.<key>]`) and carry their own
  // jurisdiction, which is why the children/detail calls deliberately do not
  // send ?jurisdiction — the worker's fast query plan is anchored off the id,
  // never off a client-supplied param.

  // `ui` (2026-09-08) is the UI language family; the worker answers with a
  // `displayName` beside every non-standard `name` where a translation exists
  // (curriculum_label_translations, migration 0038). Names and ids are
  // unchanged, so a worker that predates `ui` simply returns no displayName.
  const curriculumParams = (opts = {}, base = {}) => {
    const params = new URLSearchParams(base);
    if (opts.language && opts.language !== 'all') params.set('language', opts.language);
    if (opts.curriculum) params.set('curriculum', opts.curriculum);
    if (opts.ui) params.set('ui', opts.ui);
    return params.toString();
  };

  const fetchCurriculumTree = useCallback(async (jurisdiction, opts = {}) =>
    await apiFetch(`/api/curriculum/tree?${curriculumParams(opts, { jurisdiction })}`), [apiFetch]);

  const fetchCurriculumChildren = useCallback(async (nodeId, opts = {}) => {
    const qs = curriculumParams(opts);
    return await apiFetch(`/api/curriculum/nodes/${encodeURIComponent(nodeId)}/children${qs ? `?${qs}` : ''}`);
  }, [apiFetch]);

  // Node + ancestor chain, for the breadcrumb and the node-specific empty copy.
  // A 404 means the id no longer resolves after a re-ingest; callers clear the
  // selection instead of showing an error.
  const fetchCurriculumNode = useCallback(async (nodeId, opts = {}) => {
    const qs = curriculumParams({ ui: opts.ui });
    return await apiFetch(`/api/curriculum/nodes/${encodeURIComponent(nodeId)}${qs ? `?${qs}` : ''}`);
  }, [apiFetch]);

  // Standard-picker search: curriculum standards for a state (subject/grade/q).
  const fetchStateStandards = useCallback(async (code, opts = {}) => {
    const params = new URLSearchParams();
    if (opts.subject) params.set('subject', opts.subject);
    if (opts.gradeMin != null) params.set('grade_min', String(opts.gradeMin));
    if (opts.gradeMax != null) params.set('grade_max', String(opts.gradeMax));
    if (opts.q) params.set('q', opts.q);
    if (opts.page) params.set('page', String(opts.page));
    if (opts.limit) params.set('limit', String(opts.limit));
    const qs = params.toString();
    return await apiFetch(`/api/states/${code}/standards${qs ? `?${qs}` : ''}`);
  }, [apiFetch]);

  return {
    posters, total, totalPages, loading, error,
    fetchRootCategories, fetchCategoryChildren, fetchCategory,
    fetchPosters, searchPosters,
    fetchPoster, fetchFeaturedPosters, fetchSimilarPosters,
    purchasePrintable, fetchEntitlements,
    fetchPosterAlignment, fetchPostersAlignment, fetchStateStandards,
    submitPosterFeedback, fetchPosterFeedback, logSearch,
    fetchIntentList, fetchStateSubjects, fetchJurisdictions, fetchCatalogFacets,
    fetchCurriculumTree, fetchCurriculumChildren, fetchCurriculumNode,
  };
}
